import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Node, Edge } from 'reactflow';
import { api } from '../services/api';
import { FlowEditor } from '../components/FlowEditor/FlowEditor';
import type { FlowNodeData } from '../components/FlowEditor/types';
import { GitBranch, ArrowLeft, Save, AlertCircle, CheckCircle, Play } from 'lucide-react';

const FlowEditorPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [nodes, setNodes] = useState<Node<FlowNodeData>[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    loadDepartments();
    loadTemplates();

    if (isEditMode) {
      loadFlow();
    }
  }, [id]);

  const loadDepartments = async () => {
    try {
      const deps = await api.getDepartments();
      setDepartments(deps);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await api.getTemplates();
      const approvedTemplates = response.data.filter((t: any) => t.status === 'APPROVED');
      setTemplates(approvedTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadFlow = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const response = await api.getFlow(id);
      const flow = response.flow;

      setFlowName(flow.name);
      setFlowDescription(flow.description || '');
      setIsActive(flow.isActive);
      setIsDefault(flow.isDefault);

      // Convert backend nodes to React Flow nodes
      const flowNodes: Node<FlowNodeData>[] = flow.nodes.map((node: any) => ({
        id: node.id,
        type: 'custom',
        position: JSON.parse(node.position || '{"x": 0, "y": 0}'),
        data: {
          label: node.label || '',
          nodeType: node.nodeType,
          config: JSON.parse(node.config || '{}'),
        },
      }));

      // Convert backend edges to React Flow edges
      const flowEdges: Edge[] = flow.edges.map((edge: any) => ({
        id: edge.id,
        source: edge.fromNodeId,
        target: edge.toNodeId,
        type: 'smoothstep',
        animated: true,
        label: edge.label || undefined,
      }));

      setNodes(flowNodes);
      setEdges(flowEdges);
    } catch (error: any) {
      console.error('Error loading flow:', error);
      setError(error.response?.data?.error || 'Erro ao carregar fluxo');
    } finally {
      setIsLoading(false);
    }
  };

  const validateFlow = (): boolean => {
    const errors: string[] = [];

    // Check if flow has a name
    if (!flowName.trim()) {
      errors.push('O fluxo precisa ter um nome');
    }

    // Check if flow has at least one START node
    const startNodes = nodes.filter(n => n.data.nodeType === 'START');
    if (startNodes.length === 0) {
      errors.push('O fluxo precisa ter pelo menos um nó START');
    }
    if (startNodes.length > 1) {
      errors.push('O fluxo deve ter apenas um nó START');
    }

    // Check if flow has at least one END or CLOSE_CONVERSATION node
    const endNodes = nodes.filter(n =>
      n.data.nodeType === 'END' || n.data.nodeType === 'CLOSE_CONVERSATION'
    );
    if (endNodes.length === 0) {
      errors.push('O fluxo precisa ter pelo menos um nó END ou CLOSE_CONVERSATION');
    }

    // Check for orphan nodes (nodes without connections)
    const connectedNodeIds = new Set<string>();
    edges.forEach(edge => {
      connectedNodeIds.add(edge.source);
      connectedNodeIds.add(edge.target);
    });

    const orphanNodes = nodes.filter(n =>
      n.data.nodeType !== 'START' &&
      n.data.nodeType !== 'END' &&
      !connectedNodeIds.has(n.id)
    );

    if (orphanNodes.length > 0) {
      errors.push(`${orphanNodes.length} nó(s) não conectado(s) encontrado(s)`);
    }

    // Validate node configurations
    nodes.forEach(node => {
      const nodeLabel = node.data.label || node.data.nodeType;

      switch (node.data.nodeType) {
        case 'SEND_MESSAGE':
          if (!node.data.config.message) {
            errors.push(`${nodeLabel}: Mensagem não configurada`);
          }
          break;
        case 'SEND_TEMPLATE':
          if (!node.data.config.templateName) {
            errors.push(`${nodeLabel}: Template não selecionado`);
          }
          break;
        case 'TRANSFER_DEPARTMENT':
          if (!node.data.config.departmentId) {
            errors.push(`${nodeLabel}: Departamento não selecionado`);
          }
          break;
        case 'UPDATE_STATUS':
          if (!node.data.config.status) {
            errors.push(`${nodeLabel}: Status não configurado`);
          }
          break;
        case 'SET_VARIABLE':
          if (!node.data.config.variableName) {
            errors.push(`${nodeLabel}: Nome da variável não configurado`);
          }
          break;
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    if (!validateFlow()) {
      setError('Corrija os erros de validação antes de salvar');
      return;
    }

    try {
      setIsSaving(true);

      // Map React Flow nodes to backend format
      const backendNodes = nodes.map((node) => ({
        tempId: node.id,
        nodeType: node.data.nodeType,
        config: node.data.config,
        position: node.position,
        label: node.data.label,
      }));

      // Map React Flow edges to backend format
      const backendEdges = edges.map(edge => ({
        fromNodeId: edge.source,
        toNodeId: edge.target,
        label: edge.label || null,
        condition: null, // TODO: Add condition support
        priority: 0,
      }));

      const payload = {
        name: flowName,
        description: flowDescription || null,
        isActive,
        isDefault,
        nodes: backendNodes,
        edges: backendEdges,
      };

      if (isEditMode) {
        await api.updateFlow(id!, payload);
        setSuccess('Fluxo atualizado com sucesso!');
      } else {
        const response = await api.createFlow(payload);
        setSuccess('Fluxo criado com sucesso!');

        // Redirect to edit mode with the new flow ID
        setTimeout(() => {
          navigate(`/flows/${response.flow.id}/edit`, { replace: true });
        }, 1500);
      }
    } catch (error: any) {
      console.error('Error saving flow:', error);
      setError(error.response?.data?.error || 'Erro ao salvar fluxo');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            <button
              onClick={() => navigate('/flows')}
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <GitBranch className="w-6 h-6 text-blue-600 dark:text-blue-400" />

            <div className="flex-1 max-w-md">
              <input
                type="text"
                value={flowName}
                onChange={(e) => setFlowName(e.target.value)}
                className="w-full text-xl font-bold border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent dark:text-white"
                placeholder="Nome do Fluxo"
              />
              <input
                type="text"
                value={flowDescription}
                onChange={(e) => setFlowDescription(e.target.value)}
                className="w-full text-sm text-gray-600 dark:text-gray-400 border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent mt-1"
                placeholder="Descrição (opcional)"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700 dark:text-gray-300">Ativo</span>
            </label>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700 dark:text-gray-300">Padrão</span>
            </label>

            <button
              onClick={() => validateFlow()}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Play className="w-4 h-4" />
              Validar
            </button>

            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              {validationErrors.length > 0 && (
                <ul className="mt-2 text-sm text-red-600 dark:text-red-400 list-disc list-inside space-y-1">
                  {validationErrors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {success && (
          <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
          </div>
        )}
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-hidden">
        <FlowEditor
          initialNodes={nodes}
          initialEdges={edges}
          departments={departments}
          templates={templates}
          onNodesChange={setNodes}
          onEdgesChange={setEdges}
        />
      </div>
    </div>
  );
};

export default FlowEditorPage;
